import { getClient } from "@umami/api-client";
import {
  trackUmamiEventWithUserData,
  getCachedUserMetadata,
} from "./userMetadata";
import { isAdmin, isSuperAdmin } from "@/utils/authHelpers";

// Initialize Umami client
const client = getClient({
  endpoint: "https://cloud.umami.is/api/v1",
  apiKey: process.env.NEXT_PUBLIC_UMAMI_API_KEY,
  clientOptions: {
    headers: {
      Authorization: `Bearer ${process.env.NEXT_PUBLIC_UMAMI_API_KEY}`,
      "Content-Type": "application/json",
    },
  },
});

// Helper function to check if analytics should be disabled
const shouldDisableAnalytics = (session = null) => {
  // Skip analytics on staging environment
  if (
    typeof window !== "undefined" &&
    window.location.hostname === "staging.qensa.me"
  ) {
    return true;
  }

  if (!session?.user) return false;

  try {
    const userIsAdmin = isAdmin(session);
    const userIsSuperAdmin = isSuperAdmin(session);
    return userIsAdmin || userIsSuperAdmin;
  } catch (error) {
    console.warn("Error checking admin status:", error);
    return false;
  }
};

// Safe wrapper for direct Umami tracking calls (replaces direct window.umami.track calls)
export const safeUmamiTrack = (eventName, properties = {}) => {
  // Skip tracking on staging environment
  if (
    typeof window !== "undefined" &&
    window.location.hostname === "staging.qensa.me"
  ) {
    console.log(
      "🚫 Skipping Umami tracking on staging environment:",
      eventName
    );
    return;
  }

  if (typeof window !== "undefined" && window.umami) {
    try {
      window.umami.track(eventName, {
        timestamp: new Date().toISOString(),
        ...properties,
      });
      console.log(`📊 Analytics: ${eventName}`, properties);
    } catch (error) {
      console.warn("Analytics tracking error:", error);
    }
  }
};

// Base tracking function with safety checks (legacy - use trackEventWithUserData instead)
const safeTrack = (eventName, properties = {}, session = null) => {
  // Skip tracking for admin users
  if (shouldDisableAnalytics(session)) {
    console.log("🚫 Skipping safeTrack for admin user:", eventName);
    return;
  }

  if (typeof window !== "undefined" && window.umami) {
    try {
      window.umami.track(eventName, {
        timestamp: new Date().toISOString(),
        ...properties,
      });
      console.log(`📊 Analytics: ${eventName}`, properties);
    } catch (error) {
      console.warn("Analytics tracking error:", error);
    }
  }
};

// Enhanced tracking function with user metadata
const trackEventWithUserData = async (
  eventName,
  properties = {},
  session = null
) => {
  try {
    // Skip tracking for admin and super admin users
    if (shouldDisableAnalytics(session)) {
      console.log("🚫 Skipping analytics tracking for admin user:", {
        eventName,
        userId: session?.user?.id?.slice(0, 8) + "...",
      });
      return;
    }

    // Get user metadata if session is provided
    let userMetadata = null;
    if (session) {
      userMetadata = await getCachedUserMetadata(session);
    }

    // Use the enhanced tracking function
    trackUmamiEventWithUserData(eventName, properties, userMetadata);
  } catch (error) {
    console.warn("Enhanced analytics tracking error:", error);
    // Fallback to basic tracking
    safeTrack(eventName, properties, session);
  }
};

// User identification (deprecated - user identification now handled automatically)
export const identifyUser = (email) => {
  // This function is deprecated as user identification is now handled automatically
  // through the AnalyticsProvider. Keeping for backwards compatibility.
  console.log("📊 identifyUser called (deprecated):", email);
};

// Generic event tracking
export const trackEvent = (eventName, properties = {}, session = null) => {
  if (session) {
    trackEventWithUserData(eventName, properties, session);
  } else {
    safeTrack(eventName, properties, session);
  }
};

// NAVIGATION EVENTS
export const navigationEvents = {
  // Page visits
  pageView: (pageName, metadata = {}, session = null) =>
    trackEvent("page_view", { page: pageName, ...metadata }, session),

  // Navigation actions
  menuOpen: (menuType, metadata = {}, session = null) =>
    trackEvent("menu_open", { menu_type: menuType, ...metadata }, session),

  menuClose: (menuType, metadata = {}, session = null) =>
    trackEvent("menu_close", { menu_type: menuType, ...metadata }, session),

  // Link clicks
  linkClick: (linkType, destination, metadata = {}, session = null) =>
    trackEvent(
      "link_click",
      {
        link_type: linkType,
        destination,
        ...metadata,
      },
      session
    ),

  // Search actions
  searchSubmit: (query, resultsCount, metadata = {}, session = null) =>
    trackEvent(
      "search_submit",
      {
        query,
        results_count: resultsCount,
        ...metadata,
      },
      session
    ),
};

// USER INTERACTION EVENTS
export const userEvents = {
  // Authentication events
  signInAttempt: (method, metadata = {}, session = null) =>
    trackEvent("user_signin_attempt", { method, ...metadata }, session),

  signInSuccess: (method, metadata = {}, session = null) =>
    trackEvent("user_signin_success", { method, ...metadata }, session),

  signInError: (method, error, metadata = {}, session = null) =>
    trackEvent("user_signin_error", { method, error, ...metadata }, session),

  signUpAttempt: (method, metadata = {}, session = null) =>
    trackEvent("user_signup_attempt", { method, ...metadata }, session),

  signUpSuccess: (method, metadata = {}, session = null) =>
    trackEvent("user_signup_success", { method, ...metadata }, session),

  signUpError: (method, error, metadata = {}, session = null) =>
    trackEvent("user_signup_error", { method, error, ...metadata }, session),

  signOut: (metadata = {}, session = null) =>
    trackEvent("user_signout", metadata, session),

  // Profile actions
  profileUpdate: (field, metadata = {}, session = null) =>
    trackEvent("profile_update", { field, ...metadata }, session),

  avatarChange: (method, metadata = {}, session = null) =>
    trackEvent("avatar_change", { method, ...metadata }, session),

  // Settings changes
  settingsChange: (section, setting, value, metadata = {}, session = null) =>
    trackEvent(
      "settings_change",
      {
        section,
        setting,
        value,
        ...metadata,
      },
      session
    ),

  // Form interactions
  formStart: (formType, metadata = {}, session = null) =>
    trackEvent("form_start", { form_type: formType, ...metadata }, session),

  formSubmit: (formType, success, metadata = {}, session = null) =>
    trackEvent(
      "form_submit",
      {
        form_type: formType,
        success,
        ...metadata,
      },
      session
    ),

  formError: (formType, error, field, metadata = {}, session = null) =>
    trackEvent(
      "form_error",
      {
        form_type: formType,
        error,
        field,
        ...metadata,
      },
      session
    ),
};

// BUSINESS METRICS EVENTS
export const businessEvents = {
  // Conversion events
  trialStart: (metadata = {}, session = null) =>
    trackEvent("trial_start", metadata, session),

  subscriptionUpgrade: (fromTier, toTier, metadata = {}, session = null) =>
    trackEvent(
      "subscription_upgrade",
      {
        from_tier: fromTier,
        to_tier: toTier,
        ...metadata,
      },
      session
    ),

  subscriptionDowngrade: (fromTier, toTier, metadata = {}, session = null) =>
    trackEvent(
      "subscription_downgrade",
      {
        from_tier: fromTier,
        to_tier: toTier,
        ...metadata,
      },
      session
    ),

  subscriptionCancel: (tier, reason, metadata = {}, session = null) =>
    trackEvent(
      "subscription_cancel",
      {
        tier,
        reason,
        ...metadata,
      },
      session
    ),

  paymentAttempt: (amount, currency, tier, metadata = {}, session = null) =>
    trackEvent(
      "payment_attempt",
      {
        amount,
        currency,
        tier,
        ...metadata,
      },
      session
    ),

  paymentSuccess: (amount, currency, tier, metadata = {}, session = null) =>
    trackEvent(
      "payment_success",
      {
        amount,
        currency,
        tier,
        ...metadata,
      },
      session
    ),

  paymentFailed: (
    amount,
    currency,
    tier,
    error,
    metadata = {},
    session = null
  ) =>
    trackEvent(
      "payment_failed",
      {
        amount,
        currency,
        tier,
        error,
        ...metadata,
      },
      session
    ),

  // Feature usage
  featureUsed: (feature, context, metadata = {}, session = null) =>
    trackEvent(
      "feature_used",
      {
        feature,
        context,
        ...metadata,
      },
      session
    ),

  // Help and support
  helpRequested: (topic, method, metadata = {}, session = null) =>
    trackEvent(
      "help_requested",
      {
        topic,
        method,
        ...metadata,
      },
      session
    ),

  feedbackSubmitted: (type, rating, metadata = {}, session = null) =>
    trackEvent(
      "feedback_submitted",
      {
        type,
        rating,
        ...metadata,
      },
      session
    ),
};

// ONBOARDING TRACKING HELPERS
// Simplified onboarding tracking with consistent naming
export const trackOnboardingStep = (
  step,
  action = "view",
  metadata = {},
  session = null
) => {
  const eventName = `onboarding_${step}_${action}`;
  const eventData = {
    step,
    action,
    ...metadata,
  };

  if (session) {
    trackEventWithUserData(eventName, eventData, session);
  } else {
    safeTrack(eventName, eventData);
  }
};

// Specific onboarding events
export const onboardingEvents = {
  // Step tracking
  stepView: (step, metadata = {}, session = null) =>
    trackOnboardingStep(step, "view", metadata, session),
  stepComplete: (step, metadata = {}, session = null) =>
    trackOnboardingStep(step, "complete", metadata, session),
  stepSkip: (step, metadata = {}, session = null) =>
    trackOnboardingStep(step, "skip", metadata, session),
  stepError: (step, error, metadata = {}, session = null) =>
    trackOnboardingStep(step, "error", { error, ...metadata }, session),

  // OAuth events
  oauthAttempt: (provider, metadata = {}, session = null) =>
    trackEvent("onboarding_oauth_attempt", { provider, ...metadata }, session),
  oauthSuccess: (provider, metadata = {}, session = null) =>
    trackEvent("onboarding_oauth_success", { provider, ...metadata }, session),
  oauthError: (provider, error, metadata = {}, session = null) =>
    trackEvent(
      "onboarding_oauth_error",
      { provider, error, ...metadata },
      session
    ),

  // Email signup events
  emailSubmit: (email, metadata = {}, session = null) =>
    trackEvent("onboarding_email_submit", { email, ...metadata }, session),
  passwordSubmit: (metadata = {}, session = null) =>
    trackEvent("onboarding_password_submit", metadata, session),

  // Content selection events
  tagsSelect: (tagCount, tags = [], metadata = {}, session = null) =>
    trackEvent(
      "onboarding_tags_select",
      {
        tag_count: tagCount,
        selected_tags: tags.map((t) => t.id || t),
        ...metadata,
      },
      session
    ),
  contentSubmit: (linksCount, mediaCount, metadata = {}, session = null) =>
    trackEvent(
      "onboarding_content_submit",
      {
        links_count: linksCount,
        media_count: mediaCount,
        ...metadata,
      },
      session
    ),
  templateSelect: (templateId, metadata = {}, session = null) =>
    trackEvent(
      "onboarding_template_select",
      {
        template_id: templateId,
        ...metadata,
      },
      session
    ),

  // Completion events
  complete: (metadata = {}, session = null) =>
    trackEvent(
      "onboarding_complete",
      {
        completion_time: new Date().toISOString(),
        ...metadata,
      },
      session
    ),

  // Referral events
  referralAttempt: (referralCode, metadata = {}, session = null) =>
    trackEvent(
      "onboarding_referral_attempt",
      {
        referral_code: referralCode,
        ...metadata,
      },
      session
    ),
  referralSuccess: (referralCode, metadata = {}, session = null) =>
    trackEvent(
      "onboarding_referral_success",
      {
        referral_code: referralCode,
        ...metadata,
      },
      session
    ),
  referralError: (referralCode, error, metadata = {}, session = null) =>
    trackEvent(
      "onboarding_referral_error",
      {
        referral_code: referralCode,
        error,
        ...metadata,
      },
      session
    ),
};

// SPACE PUBLISHING TRACKING HELPERS
// Comprehensive publishing funnel tracking
export const publishingEvents = {
  // Publishing attempt tracking
  publishAttempt: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_publish_attempt",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  // Publishing success
  publishSuccess: (
    spaceId,
    spaceSlug,
    version,
    metadata = {},
    session = null
  ) =>
    trackEvent(
      "space_publish_success",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        version_number: version,
        ...metadata,
      },
      session
    ),

  // Publishing errors/failures
  publishError: (
    spaceId,
    spaceSlug,
    error,
    step = null,
    metadata = {},
    session = null
  ) =>
    trackEvent(
      "space_publish_error",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        error_message: error,
        error_step: step,
        ...metadata,
      },
      session
    ),

  // Payment-related publishing events
  publishPaymentRequired: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_publish_payment_required",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  publishPaymentInitiated: (
    spaceId,
    spaceSlug,
    metadata = {},
    session = null
  ) =>
    trackEvent(
      "space_publish_payment_initiated",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  publishPaymentSuccess: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_publish_payment_success",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  publishPaymentFailed: (
    spaceId,
    spaceSlug,
    error,
    metadata = {},
    session = null
  ) =>
    trackEvent(
      "space_publish_payment_failed",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        error_message: error,
        ...metadata,
      },
      session
    ),

  publishPaymentCancelled: (
    spaceId,
    spaceSlug,
    metadata = {},
    session = null
  ) =>
    trackEvent(
      "space_publish_payment_cancelled",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  // Publishing tray/modal interactions
  publishTrayOpen: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_publish_tray_open",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  publishTrayClose: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_publish_tray_close",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  publishTrayCancel: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_publish_tray_cancel",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  // Pre-publish actions
  publishUrlCopy: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_publish_url_copy",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  // Publishing flow abandonment
  publishAbandoned: (spaceId, spaceSlug, step, metadata = {}, session = null) =>
    trackEvent(
      "space_publish_abandoned",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        abandonment_step: step,
        ...metadata,
      },
      session
    ),

  // Draft expiration related
  publishDraftExpired: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_publish_draft_expired",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  // Slug change during publish
  publishSlugChange: (
    spaceId,
    oldSlug,
    newSlug,
    metadata = {},
    session = null
  ) =>
    trackEvent(
      "space_publish_slug_change",
      {
        space_id: spaceId,
        old_slug: oldSlug,
        new_slug: newSlug,
        ...metadata,
      },
      session
    ),

  // Pricing modal specific events
  pricingModalView: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "pricing_modal_view",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  pricingModalPayNowClick: (
    spaceId,
    spaceSlug,
    metadata = {},
    session = null
  ) =>
    trackEvent(
      "pricing_modal_pay_now_click",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  pricingModalPayLaterClick: (
    spaceId,
    spaceSlug,
    metadata = {},
    session = null
  ) =>
    trackEvent(
      "pricing_modal_pay_later_click",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  pricingModalBillingToggle: (
    spaceId,
    spaceSlug,
    frequency,
    metadata = {},
    session = null
  ) =>
    trackEvent(
      "pricing_modal_billing_toggle",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        billing_frequency: frequency,
        ...metadata,
      },
      session
    ),

  pricingModalClose: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "pricing_modal_close",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),
};

// SPACE INTERACTION TRACKING
export const spaceEvents = {
  // Space editing events
  spaceEdit: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_edit",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  spacePreview: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_preview",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  spaceView: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_view",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  spaceShare: (spaceId, spaceSlug, method, metadata = {}, session = null) =>
    trackEvent(
      "space_share",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        method,
        ...metadata,
      },
      session
    ),

  // Widget interactions
  widgetAdd: (spaceId, widgetType, metadata = {}, session = null) =>
    trackEvent(
      "space_widget_add",
      {
        space_id: spaceId,
        widget_type: widgetType,
        ...metadata,
      },
      session
    ),

  widgetEdit: (spaceId, widgetType, widgetId, metadata = {}, session = null) =>
    trackEvent(
      "space_widget_edit",
      {
        space_id: spaceId,
        widget_type: widgetType,
        widget_id: widgetId,
        ...metadata,
      },
      session
    ),

  widgetDelete: (
    spaceId,
    widgetType,
    widgetId,
    metadata = {},
    session = null
  ) =>
    trackEvent(
      "space_widget_delete",
      {
        space_id: spaceId,
        widget_type: widgetType,
        widget_id: widgetId,
        ...metadata,
      },
      session
    ),

  widgetMove: (spaceId, widgetType, widgetId, metadata = {}, session = null) =>
    trackEvent(
      "space_widget_move",
      {
        space_id: spaceId,
        widget_type: widgetType,
        widget_id: widgetId,
        ...metadata,
      },
      session
    ),

  widgetDuplicate: (
    spaceId,
    widgetType,
    widgetId,
    metadata = {},
    session = null
  ) =>
    trackEvent(
      "space_widget_duplicate",
      {
        space_id: spaceId,
        widget_type: widgetType,
        widget_id: widgetId,
        ...metadata,
      },
      session
    ),

  // Theme changes
  themeChange: (spaceId, themeType, metadata = {}, session = null) =>
    trackEvent(
      "space_theme_change",
      {
        space_id: spaceId,
        theme_type: themeType,
        ...metadata,
      },
      session
    ),

  // Layout changes
  layoutChange: (spaceId, layoutType, metadata = {}, session = null) =>
    trackEvent(
      "space_layout_change",
      {
        space_id: spaceId,
        layout_type: layoutType,
        ...metadata,
      },
      session
    ),

  // Sharing events
  shareUrlCopy: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_share_copy",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  // Space management
  spaceCreate: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_create",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  spaceDelete: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_delete",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  spaceDuplicate: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_duplicate",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  // Settings changes
  settingsOpen: (spaceId, spaceSlug, metadata = {}, session = null) =>
    trackEvent(
      "space_settings_open",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        ...metadata,
      },
      session
    ),

  settingsChange: (
    spaceId,
    spaceSlug,
    setting,
    value,
    metadata = {},
    session = null
  ) =>
    trackEvent(
      "space_settings_change",
      {
        space_id: spaceId,
        space_slug: spaceSlug,
        setting,
        value,
        ...metadata,
      },
      session
    ),
};

// EXISTING ANALYTICS FUNCTIONS (kept for backwards compatibility)
export const getSpaceAnalytics = async (spaceSlug) => {
  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    // Build query parameters
    const params = new URLSearchParams({
      startAt: startDate.getTime().toString(),
      endAt: endDate.getTime().toString(),
      url: `${spaceSlug}`,
      unit: "day",
    });

    // Get pageviews data with query parameters
    const pageviewResponse = await fetch(
      `https://api.umami.is/api/websites/${process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID}/pageviews?${params}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_UMAMI_API_KEY}`,
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-umami-api-key": process.env.NEXT_PUBLIC_UMAMI_API_KEY,
        },
      }
    );

    console.log(pageviewResponse);

    console.log("Response status:", pageviewResponse.status);
    console.log("Response headers:", pageviewResponse.headers);
    const responseText = await pageviewResponse.text();
    console.log("Response text:", responseText);

    if (!pageviewResponse.ok) {
      throw new Error(`HTTP error! status: ${pageviewResponse.status}`);
    }

    const pageviewData = JSON.parse(responseText);

    // Build stats query parameters
    const statsParams = new URLSearchParams({
      startAt: startDate.getTime().toString(),
      endAt: endDate.getTime().toString(),
      url: `${spaceSlug}`,
    });

    // Get stats data with query parameters
    const statsResponse = await fetch(
      `https://api.umami.is/api/websites/${process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID}/stats?${statsParams}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_UMAMI_API_KEY}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!statsResponse.ok) {
      throw new Error(`HTTP error! status: ${statsResponse.status}`);
    }

    const statsData = await statsResponse.json();

    // Transform pageview data
    const pageViews = pageviewData.map((item) => ({
      date: item.x,
      views: item.y,
    }));

    return {
      pageViews,
      totalViews: pageViews.reduce((sum, item) => sum + item.views, 0),
      uniqueVisitors: statsData.uniques || 0,
      averageTime: Math.round(statsData.avgDuration || 0),
    };
  } catch (error) {
    console.error("Error fetching analytics:", error);
    return {
      pageViews: [],
      totalViews: 0,
      uniqueVisitors: 0,
      averageTime: 0,
    };
  }
};

export const getActiveVisitors = async () => {
  try {
    const data = await client.getWebsiteActive(
      process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID
    );
    return data;
  } catch (error) {
    console.error("Error fetching active visitors:", error);
    return 0;
  }
};

export const getEventMetrics = async (eventName, startAt, endAt) => {
  try {
    const data = await client.getWebsiteMetrics(
      process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID,
      {
        startAt,
        endAt,
        type: "event",
        name: eventName,
      }
    );
    return data;
  } catch (error) {
    console.error("Error fetching event metrics:", error);
    return [];
  }
};

export const getSpaceEvents = async (spaceSlug, startAt, endAt) => {
  try {
    const data = await client.getWebsiteEvents(
      process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID,
      {
        startAt,
        endAt,
        url: `/${spaceSlug}`,
      }
    );
    return data;
  } catch (error) {
    console.error("Error fetching space events:", error);
    return [];
  }
};
